"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import posthog from "posthog-js";
import { reportClientError } from "@/lib/client-error-report";
import {
  ChunkReloadNotice,
  StaleDeployReloadPanel,
  useChunkReloadRecovery,
} from "@/lib/use-chunk-reload-recovery";

export default function ErrorPage({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const pathname = usePathname();

  // Stale-deploy auto-recovery (lib/chunk-load-recovery.ts): after a deploy,
  // an already-open tab lazy-loading a renamed chunk throws ChunkLoadError,
  // and invoking a Server Action from stale HTML throws
  // UnrecognizedActionError. reset() cannot recover either (it re-renders
  // the same tree); only a full reload fetches fresh HTML with current chunk
  // references and action IDs. The hook performs a one-time guarded reload;
  // recurrences within the cooldown (a real outage, not deploy skew) fall
  // through to the normal error UI + reporting below.
  const { autoReloading, staleDeployError } = useChunkReloadRecovery(error);

  useEffect(() => {
    if (autoReloading) return; // transient deploy skew — heals on reload, don't alert
    reportClientError(pathname, error);
  }, [autoReloading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (autoReloading) return; // see above — suppressed for the auto-healing pass
    if (posthog.__loaded) {
      // captureException emits a proper `$exception_list` payload so this
      // render error is grouped into a PostHog Error Tracking Issue. (The old
      // posthog.capture("$exception", { $exception_message, ... }) call never
      // formed an Issue.) React render errors never reach window.onerror, so
      // exception autocapture in the provider does NOT cover this path — the
      // explicit captureException here is required.
      posthog.captureException(error, {
        route: pathname,
        digest: error.digest,
        staleDeployError,
      });
    }
  }, [error, pathname, autoReloading, staleDeployError]);

  if (autoReloading) return <ChunkReloadNotice />;
  // Non-recoverable stale-deploy error (guard couldn't persist, or a reload
  // already failed): show an explicit reload action, not a bare "Something
  // went wrong". The reporting effects above still ran.
  //
  // This is the LAST boundary — nothing catches below it — so it gets an
  // escape too, even though its ordinary UI never had a link. A user whose
  // browser refuses the asset host would otherwise have only a reload that
  // loops forever.
  if (staleDeployError) {
    return (
      <StaleDeployReloadPanel
        fullScreen
        escape={{ href: "/", label: "Go to homepage" }}
      />
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 text-center">
      <div className="max-w-md space-y-4">
        <h1 className="text-2xl font-bold text-foreground">
          The Site Book
        </h1>
        <h2 className="text-xl font-semibold text-foreground">
          Something went wrong
        </h2>
        <p className="text-muted">
          We hit an unexpected issue. Please try again, and if the problem
          persists, contact support.
        </p>
        <button
          onClick={() => reset()}
          className="inline-flex items-center justify-center rounded-lg bg-accent px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-accent-dark"
        >
          Try again
        </button>
      </div>
    </div>
  );
}
